import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import { AnchorEl, FixMeLater, ListingV2, PermissionApiName } from 'src/types';
import { formatCurrency, formatDate, getStatusColor } from 'src/utils';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { ListingMenuEnum } from 'src/enums';
import {
  LinkComponent,
  ListingFreebieChip,
  LoaderBar,
  RmaChip
} from 'src/components';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { cloneDeep } from 'lodash';
import { usePermissions } from 'src/hooks';
import { multiBranchFeat } from 'src/constants/feature-toggle';
import useResolution from 'src/hooks/useResolution';
import { TextRow } from './TextRow';
import { AlternateRowContainer } from './AlternateRowContainer';
import ReasonDialog from 'src/components/product/ReasonDialog';
import {
  appendInternalRemarks,
  formatInternalRemarks
} from 'src/utils/remarks/remarks';
import { statusActionMap } from 'src/constants';

type MenuItemsType = {
  type: ListingMenuEnum;
  text: string;
  permissions?: PermissionApiName[];
};

const menuItems: MenuItemsType[] = [
  {
    type: ListingMenuEnum.Edit,
    text: 'View Details / Edit',
    permissions: ['can_update_listing']
  },
  {
    type: ListingMenuEnum.ViewHistory,
    text: 'View Updates History',
    permissions: ['can_update_listing']
  },
  {
    type: ListingMenuEnum.SetOnHold,
    text: 'Set as `Hold`',
    permissions: ['can_update_listing']
  },
  {
    type: ListingMenuEnum.SetAvailable,
    text: 'Set as `Available`',
    permissions: ['can_update_listing']
  },
  {
    type: ListingMenuEnum.SetPurchased,
    text: 'Set as `Purchased`',
    permissions: ['can_update_listings_status']
  },
  {
    type: ListingMenuEnum.SetDefective,
    text: 'Set as `Defective`',
    permissions: ['can_update_listings_status', 'can_update_listings_defective']
  },
  {
    type: ListingMenuEnum.SetLoss,
    text: 'Set as `Loss`',
    permissions: ['can_update_listings_status']
  },
  //TODO: Rma is deprecated, remove later
  // { type: ListingMenuEnum.SetForRma, text: 'Set as `RMA`' },
  {
    type: ListingMenuEnum.Delete,
    text: 'Delete',
    permissions: ['can_delete_listing']
  }
];

interface Props {
  isLoading?: boolean;
  listings: ListingV2[];
  anchorEls: AnchorEl;
  onCopySerialNo: (e: any, a: string) => void;
  handleActionClose: (
    item: ListingV2,
    type?: ListingMenuEnum,
    reason?: string
  ) => void;
  handleActionClick: (menuId: number, event: FixMeLater) => void;
}

const useStyles = makeStyles((theme) => ({
  consumableAlert: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  statusContainer: {
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  mainDetails: {
    display: 'flex',
    rowGap: '.1em',
    flexDirection: 'column',
    padding: '1em'
  }
}));

const Listings = ({
  listings,
  isLoading,
  anchorEls,
  onCopySerialNo,
  handleActionClick,
  handleActionClose
}: Props) => {
  const classes = useStyles();
  const {
    userPermissionsStringArray,
    canSeeDealersPrice,
    canViewPurchaseOrder
  } = usePermissions();
  const { isMobile, xs } = useResolution();

  const [isReasonDialogVisible, setIsReasonDialogVisible] = useState(false);
  const [selectedListing, setSelectedListing] = useState<ListingV2 | null>(
    null
  );

  const tooltipPurchasedBy = useCallback((item: ListingV2) => {
    if (item?.purchase_info?.full_name) {
      return `Purchased by ${item?.purchase_info?.full_name}`;
    }
    return '';
  }, []);

  const isMenuItemDisabled = (item: any, type: any) => {
    if (!type) return false;

    const map: any = statusActionMap;
    const targetStatus = map[type];

    if (!targetStatus) return false;

    return item.status === targetStatus;
  };

  const menuItemWithPermissions = useMemo(() => {
    const clonedItems = cloneDeep(menuItems || []);
    let itemsWillAppear: MenuItemsType[] = clonedItems;
    itemsWillAppear = itemsWillAppear?.filter((x) => {
      if (x?.permissions && x?.permissions?.length > 0) {
        return x?.permissions?.some((r) =>
          userPermissionsStringArray?.includes(r)
        );
      }
      return true;
    });
    return itemsWillAppear;
  }, [userPermissionsStringArray]);

  const handleMenuItemClick = (item: ListingV2, type?: ListingMenuEnum) => {
    if (type === ListingMenuEnum.SetOnHold) {
      setSelectedListing(item);
      setIsReasonDialogVisible(true);
      return;
    }

    handleActionClose(item, type);
  };

  const handleSaveReason = (reason: string) => {
    if (!selectedListing) return;

    const newRemark = formatInternalRemarks({
      remarks: reason
    });

    const updatedRemarks = appendInternalRemarks(
      selectedListing.internal_remarks,
      newRemark
    );

    handleActionClose(
      selectedListing,
      ListingMenuEnum.SetOnHold,
      updatedRemarks
    );

    setSelectedListing(null);
    setIsReasonDialogVisible(false);
  };

  const handleCloseReasonDialog = () => {
    setSelectedListing(null);
    setIsReasonDialogVisible(false);
  };

  return (
    <>
      {isMobile ? (
        <Box marginY={2}>
          <Card>
            <Divider />
            <PerfectScrollbar>
              <Box>
                {listings.map((item: ListingV2, ind) => (
                  <AlternateRowContainer key={ind} index={ind}>
                    <div className={classes.mainDetails}>
                      <TextRow label="DR No." value={item?.dr_no} />
                      <TextRow
                        label="Serial No"
                        component={
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              maxWidth: xs ? '230px' : undefined,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word'
                            }}
                          >
                            <Button
                              onClick={(e) =>
                                onCopySerialNo(e, item?.serial_no ?? '')
                              }
                            >
                              <FileCopyIcon />
                            </Button>
                            <LinkComponent
                              openInNewTab
                              href={`/app/products-listings/${item.id}`}
                              title={`${item?.serial_no ?? '--'}`}
                              font="Roboto, Helvetica, Arial, sans-serif "
                            />
                            <ListingFreebieChip listing={item} />
                          </div>
                        }
                      />
                      <TextRow
                        label="P.O. No."
                        component={
                          <div>
                            {canViewPurchaseOrder && item.po_no ? (
                              <LinkComponent
                                openInNewTab
                                href={`/app/purchase-order/${item.po_id}`}
                                title={`${item?.po_no}`}
                              />
                            ) : (
                              <Typography>{item?.po_no ?? '--'}</Typography>
                            )}
                          </div>
                        }
                      />
                      <TextRow label="Supplier" value={item?.supplier?.name} />
                      <TextRow label="Branch" value={item?.branch} />
                      {canSeeDealersPrice && (
                        <TextRow
                          label="Dealers Price"
                          value={formatCurrency(item?.dealers_price)}
                        />
                      )}
                      <TextRow
                        label="Price"
                        value={formatCurrency(item?.retail_price)}
                      />
                      <TextRow
                        label="Date Purchased (Supplier)"
                        value={formatDate(item?.purchase_date)}
                      />
                      <TextRow
                        label="Date Encoded"
                        value={formatDate(item?.created_at)}
                      />
                      <TextRow
                        label="Status"
                        component={
                          <div className={classes.statusContainer}>
                            <Typography
                              style={{ color: getStatusColor(item?.status) }}
                            >
                              {item.status}
                            </Typography>
                            {item?.rma ? <RmaChip rma={item?.rma} /> : null}
                          </div>
                        }
                      />
                      <TextRow
                        label="Action"
                        noBorderBottom
                        component={
                          <>
                            <IconButton
                              aria-label="more"
                              aria-controls="long-menu"
                              aria-haspopup="true"
                              onClick={(e) => handleActionClick(item.id, e)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <Menu
                              id={item.id.toString()}
                              anchorEl={anchorEls[item.id]}
                              keepMounted
                              open={Boolean(anchorEls[item.id])}
                              onClose={() => handleActionClose(item)}
                            >
                              {menuItemWithPermissions
                                ?.filter((menuItem) => {
                                  if (menuItem?.type === 'DELETE') {
                                    return item?.status === 'available';
                                  }
                                  return true;
                                })
                                .map((menuItem, i) => (
                                  <MenuItem
                                    key={menuItem?.type || i}
                                    disabled={isMenuItemDisabled(
                                      item,
                                      menuItem?.type
                                    )}
                                    onClick={() =>
                                      handleMenuItemClick(item, menuItem?.type)
                                    }
                                  >
                                    {menuItem?.text}
                                  </MenuItem>
                                ))}
                            </Menu>
                          </>
                        }
                      />
                    </div>
                  </AlternateRowContainer>
                ))}
              </Box>
            </PerfectScrollbar>
          </Card>
        </Box>
      ) : (
        <Box marginY={2}>
          <Card>
            <Divider />
            <PerfectScrollbar>
              <Box>
                <LoaderBar isLoading={isLoading ? isLoading : false} />
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>DR No.</TableCell>
                      <TableCell>Serial No</TableCell>
                      <TableCell>P.O. No.</TableCell>
                      <TableCell>Supplier</TableCell>
                      {multiBranchFeat ? <TableCell>Branch</TableCell> : null}
                      {canSeeDealersPrice ? (
                        <TableCell>Dealers Price</TableCell>
                      ) : null}
                      <TableCell>Price</TableCell>
                      <TableCell>Date Purchased (Supplier)</TableCell>
                      <TableCell>Date Encoded</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {listings.map((item: ListingV2) => (
                      <TableRow hover key={item.id}>
                        <TableCell>{item?.dr_no ?? '--'}</TableCell>
                        <TableCell
                          style={{
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <LinkComponent
                            openInNewTab
                            href={`/app/products-listings/${item.id}`}
                            title={`${item?.serial_no ?? '--'}`}
                          />
                          <Button
                            startIcon={<FileCopyIcon />}
                            onClick={(e) =>
                              onCopySerialNo(e, item?.serial_no ?? '')
                            }
                          />
                          <ListingFreebieChip listing={item} />
                        </TableCell>
                        <TableCell>
                          {canViewPurchaseOrder && item.po_no ? (
                            <LinkComponent
                              openInNewTab
                              href={`/app/purchase-order/${item.po_id}`}
                              title={`${item?.po_no}`}
                            />
                          ) : (
                            item?.po_no ?? '--'
                          )}
                        </TableCell>
                        <TableCell>{item?.supplier?.name ?? '--'}</TableCell>
                        {multiBranchFeat ? (
                          <TableCell>{item?.branch ?? '--'}</TableCell>
                        ) : null}
                        {canSeeDealersPrice ? (
                          <TableCell>
                            {`DP ${formatCurrency(item?.dealers_price) ??
                              '--'}`}
                          </TableCell>
                        ) : null}
                        <TableCell>
                          {`SRP ${formatCurrency(item?.retail_price) ?? '--'}`}
                        </TableCell>
                        <TableCell>{formatDate(item?.purchase_date)}</TableCell>
                        <TableCell>{formatDate(item?.created_at)}</TableCell>
                        <TableCell>
                          <Tooltip title={tooltipPurchasedBy(item)}>
                            <div className={classes.statusContainer}>
                              <Typography
                                style={{
                                  color: getStatusColor(item?.status),
                                  fontWeight:
                                    item.status === 'loss' ? 'bolder' : 'normal'
                                }}
                              >
                                {item.status}
                              </Typography>
                              {item?.rma ? <RmaChip rma={item?.rma} /> : null}
                            </div>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            aria-label="more"
                            aria-controls="long-menu"
                            aria-haspopup="true"
                            onClick={(e) => handleActionClick(item.id, e)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            id={item.id.toString()}
                            anchorEl={anchorEls[item.id]}
                            keepMounted
                            open={Boolean(anchorEls[item.id])}
                            onClose={() => handleActionClose(item)}
                          >
                            {menuItemWithPermissions
                              ?.filter((menuItem) => {
                                if (menuItem?.type === 'DELETE') {
                                  return item?.status === 'available';
                                }
                                return true;
                              })
                              .map((menuItem, i) => (
                                <MenuItem
                                  key={menuItem?.type || i}
                                  disabled={isMenuItemDisabled(
                                    item,
                                    menuItem?.type
                                  )}
                                  onClick={() =>
                                    handleMenuItemClick(item, menuItem?.type)
                                  }
                                >
                                  {menuItem?.text}
                                </MenuItem>
                              ))}
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </PerfectScrollbar>
          </Card>
        </Box>
      )}
      <ReasonDialog
        title="Set as On Hold"
        subTitle="Please provide the reason for setting this listing as On Hold."
        label="Reason"
        placeholder="Enter reason"
        isVisible={isReasonDialogVisible}
        handleClose={handleCloseReasonDialog}
        onSavePress={handleSaveReason}
      />
      ;
    </>
  );
};

export const SerializedListingsProductTable = React.memo(Listings);
